import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import moment from 'moment';

import { FieldTypeEnum } from '@models/module/fields/enums';
import { ScreenFieldModel } from '@models/module/fields/main';
import { DataCard } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { CalendarTileDoc } from '@module-instance/calendar/calendar.model';
import { DataCardViewComponent } from '@module-instance/data-card/data-card-view/data-card-view.component';
import { DataCardsViewService } from '@module-instance/data-card/data-cards-view/data-cards-view.service';
import { DropdownActionsEnum } from '@shared/consts';
import { CipoDialogModule } from '@shared/modules';
import { BREAKPOINTS_MEDIA_QUERY } from 'src/app-breakpoints.module';

export const CalendarDialogDocDetailPrefix: string = 'calendar-diaog-doc-detail';
export const CalendarDialogDocDetailInnerWidth: number = 600;
export const CalendarDialogDocDetailOuterWidth: number = 648;
export const CalendarDialogDocDetailInnerHeight: number = 250;
export const CalendarDialogDocDetailOuterHeight: number = 335;

/** Larger size used when the card has a Table/Weather field, so its rows aren't cramped. */
export const CalendarDialogDocDetailWideInnerWidth: number = 900;
export const CalendarDialogDocDetailWideOuterWidth: number = 948;
export const CalendarDialogDocDetailWideInnerHeight: number = 520;
export const CalendarDialogDocDetailWideOuterHeight: number = 605;

export function hasTableOrWeatherField(screenFields: ScreenFieldModel[]): boolean {
  return (screenFields || []).some(f => f.typeId === FieldTypeEnum.Table || f.typeId === FieldTypeEnum.Weather);
}

@Component({
  selector: 'app-calendar-dialog-doc-detail',
  templateUrl: './calendar-dialog-doc-detail.component.html',
  styleUrls: ['./calendar-dialog-doc-detail.component.scss'],
  host: {
    '[style.min-width]': 'minWidth',
    '[style.max-width]': 'maxWidth',
  },
  imports: [CommonModule, MatMenuModule, CipoDialogModule, DataCardViewComponent],
})
export class CalendarDialogDocDetailComponent implements OnInit {
  private readonly dataCardsViewService = inject(DataCardsViewService);
  private readonly dialog = inject(MatDialog);
  private readonly breakpointObserver = inject(BreakpointObserver);
  readonly data = inject<{
    doc: CalendarTileDoc;
    screenFields: ScreenFieldModel[];
    actions: DataListViewAction[];
  }>(MAT_DIALOG_DATA);

  card: DataCard;
  actionsFiltered: DataListViewAction[];
  private isSmallScreen = false;

  constructor() {
    this.breakpointObserver.observe([BREAKPOINTS_MEDIA_QUERY.lt_md]).subscribe(() => {
      this.isSmallScreen = true;
    });
  }

  ngOnInit(): void {
    this.card = this.dataCardsViewService.getDataCard(
      this.data.screenFields,
      this.data.doc.ref,
      this.data.doc.currency,
    );

    if ((this.data.actions || []).length && this.card !== undefined) {
      this.actionsFiltered = (this.data.actions || []).filter(
        a => a.alwaysOnRow === true && a.condition !== undefined && a.condition(this.card),
      );
    } else {
      this.actionsFiltered = [];
    }
  }

  get isWide(): boolean {
    return hasTableOrWeatherField(this.data.screenFields);
  }

  get minWidth(): string {
    return this.isSmallScreen ? 'inherit' : `${this.innerWidth}px`;
  }

  get maxWidth(): string {
    return this.isSmallScreen ? 'inherit' : `${this.innerWidth}px`;
  }

  private get innerWidth(): number {
    return this.isWide ? CalendarDialogDocDetailWideInnerWidth : CalendarDialogDocDetailInnerWidth;
  }

  get detailContentMaxHeight(): number {
    return this.isWide ? CalendarDialogDocDetailWideInnerHeight : CalendarDialogDocDetailInnerHeight;
  }

  get datetime(): string {
    const dt = moment(this.data.doc.ref.date);
    const isMidnight = dt.isSame(dt.clone().startOf('day'));
    return dt.format(isMidnight ? 'LL' : 'LLL');
  }

  actionsMenuClick($event: Event) {
    $event.stopPropagation();
  }

  actionClick(action: DataListViewAction) {
    if (action.click) {
      this.dialog.closeAll();
      action.click(this.data.doc.ref);
    }
  }

  cardViewClick() {
    const clickableActionTypes = [DropdownActionsEnum.VIEW.toString(), DropdownActionsEnum.EDIT.toString()];
    const action = this.actionsFiltered.find(a => clickableActionTypes.includes(a.actionType));
    if (action) {
      this.actionClick(action);
    }
  }
}
